// @ts-nocheck
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileUpload } from '../components/FileUpload';
import { ImportPreview } from '../components/ImportPreview';
import { api } from '../lib/api';
import { CheckCircle } from 'lucide-react';

export function Import() {
  const [step, setStep] = useState<'upload' | 'preview' | 'done'>('upload');
  const [preview, setPreview] = useState<Record<string, unknown> | null>(null);
  const [filename, setFilename] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState('');
  const fileRef = useRef<File | null>(null);
  const navigate = useNavigate();

  const handleFile = async (file: File) => {
    setError('');
    setUploading(true);
    fileRef.current = file;
    try {
      const res = await api.uploadXlsx(file);
      setPreview(res.preview as Record<string, unknown>);
      setFilename(res.filename as string);
      const rows = (res.preview as { rows: { year: number }[] }).rows;
      if (rows.length > 0) setYear(rows[0].year);
      setStep('preview');
    } catch (err) {
      setError(String((err as Error).message));
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    if (!fileRef.current) {
      setError('File lost — please re-upload');
      setStep('upload');
      return;
    }
    setImporting(true);
    setError('');
    try {
      const res = await api.confirmImport(fileRef.current, filename, year);
      setResult(res);
      setStep('done');
      // Auto-redirect to dashboard after 2 seconds
      setTimeout(() => navigate('/wht'), 2000);
    } catch (err) {
      setError(String((err as Error).message));
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setStep('upload');
    setPreview(null);
    setFilename('');
    setResult(null);
    setError('');
    fileRef.current = null;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Import Data</h1>
          <p className="text-sm text-gray-500 mt-1">
            Upload WHT transaction data from your accounting software (XLSX export)
          </p>
        </div>
        {step === 'preview' && (
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-500">Year:</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="border rounded px-3 py-1.5 text-sm"
            >
              {[2025, 2024, 2023].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {step === 'upload' && (
        <div className="max-w-xl mx-auto mt-12">
          <FileUpload
            onFile={handleFile}
            label={uploading ? 'Parsing file...' : 'Upload XLSX File'}
          />
          <p className="text-xs text-gray-400 mt-3 text-center">
            Supported format: PEAK accounting software WHT export (.xlsx)
          </p>
        </div>
      )}

      {step === 'preview' && preview && (
        <ImportPreview
          preview={preview as Parameters<typeof ImportPreview>[0]['preview']}
          onConfirm={handleConfirm}
          onCancel={handleReset}
          loading={importing}
        />
      )}

      {step === 'done' && result && (
        <div className="max-w-md mx-auto mt-12 text-center">
          <CheckCircle className="mx-auto text-green-500 mb-4" size={48} />
          <h2 className="text-xl font-bold text-green-700">Import Successful!</h2>
          <p className="text-gray-600 mt-2">
            {result.imported as number} transactions imported successfully.
          </p>
          <div className="mt-6 flex gap-3 justify-center">
            <button
              onClick={handleReset}
              className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Import Another File
            </button>
            <a
              href="/wht/clients"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              View Clients
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
